import styled from 'styled-components'
import { omniaTheme } from '../../theme/omniaTheme'

// ============================================================
// Page-level wrapper
// ============================================================

export const LocationContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 14mm 14mm 14mm 14mm;
  box-sizing: border-box;

  @media print {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 14mm 14mm 14mm 14mm !important;
  }
`

// ============================================================
// Two-column grid: map + insight cards
// ============================================================

export const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  /* Explicit aspect-ratio drives the height of the children — never flex:1 */
  aspect-ratio: 515 / 480;
  width: 100%;

  @media print {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 20px !important;
    aspect-ratio: 515 / 480 !important;
    width: 100% !important;
  }
`

// ============================================================
// LEFT — Satellite map (explicit height via aspect-ratio on parent)
// ============================================================

export const MapWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #a8b89c;

  @media print {
    position: relative !important;
    width: 100% !important;
    height: 100% !important;
    overflow: hidden !important;
    background: #a8b89c !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const MapImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;

  @media print {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    display: block !important;
  }
`

export const MapPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #a8b89c;
  border: 0.5px dashed #8fa084;
  box-sizing: border-box;
  font-family: ${omniaTheme.fonts.body};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: #5f6e55;

  @media print {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: #a8b89c !important;
    border: 0.5px dashed #8fa084 !important;
    color: #5f6e55 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/**
 * Purple location pin overlaid on the map, centered. Teardrop shape with
 * a white dot at the head. Rendered as an inline SVG via this wrapper.
 */
export const MapPinWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 30px;
  pointer-events: none;

  @media print {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: 24px !important;
    height: 30px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// RIGHT — 3 stacked white insight cards
// ============================================================

export const InsightsColumn = styled.div`
  display: grid;
  /* 3 cards with proportional heights matching the mockup (132/184/132). */
  grid-template-rows: 132fr 184fr 132fr;
  gap: 16px;
  height: 100%;
  min-height: 0;

  @media print {
    display: grid !important;
    grid-template-rows: 132fr 184fr 132fr !important;
    gap: 16px !important;
    height: 100% !important;
  }
`

export const InsightCard = styled.div`
  background: ${omniaTheme.colors.cardBg};
  padding: 20px;
  border: 0;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media print {
    background: ${omniaTheme.colors.cardBg} !important;
    padding: 20px !important;
    border: 0 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  @media print {
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
    margin-bottom: 12px !important;
  }
`

export const CardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${omniaTheme.colors.primary};

  @media print {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 16px !important;
    height: 16px !important;
    color: ${omniaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CardTitle = styled.h3`
  font-family: ${omniaTheme.fonts.heading};
  font-weight: ${omniaTheme.fontWeights.medium};
  font-size: 14px;
  color: ${omniaTheme.colors.black};
  margin: 0;
  line-height: 1;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-weight: ${omniaTheme.fontWeights.medium} !important;
    font-size: 14px !important;
    color: ${omniaTheme.colors.black} !important;
    line-height: 1 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const EntryList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 !important;
  }
`

export const EntryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 0.5px solid ${omniaTheme.colors.border};
  }

  &:first-child {
    padding-top: 4px;
  }

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
    padding: 8px 0 !important;

    &:not(:last-child) {
      border-bottom: 0.5px solid ${omniaTheme.colors.border} !important;
    }

    &:first-child {
      padding-top: 4px !important;
    }
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const EntryLabel = styled.span`
  font-family: ${omniaTheme.fonts.label};
  font-weight: ${omniaTheme.fontWeights.light};
  font-size: 8px;
  color: ${omniaTheme.colors.textLabel};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  text-transform: uppercase;
  line-height: 1.2;

  @media print {
    font-family: ${omniaTheme.fonts.label} !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    font-size: 8px !important;
    color: ${omniaTheme.colors.textLabel} !important;
    letter-spacing: ${omniaTheme.letterSpacing.wide} !important;
    text-transform: uppercase !important;
    line-height: 1.2 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const EntryRow2 = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;

  @media print {
    display: flex !important;
    align-items: baseline !important;
    justify-content: space-between !important;
    gap: 8px !important;
  }
`

export const EntryName = styled.span`
  font-family: ${omniaTheme.fonts.body};
  font-weight: ${omniaTheme.fontWeights.medium};
  font-size: 11px;
  color: ${omniaTheme.colors.black};
  line-height: 1.3;

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-weight: ${omniaTheme.fontWeights.medium} !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.black} !important;
    line-height: 1.3 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const EntryDistance = styled.span`
  font-family: ${omniaTheme.fonts.body};
  font-weight: ${omniaTheme.fontWeights.light};
  font-size: 10px;
  color: ${omniaTheme.colors.text};
  white-space: nowrap;
  flex-shrink: 0;

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    font-size: 10px !important;
    color: ${omniaTheme.colors.text} !important;
    white-space: nowrap !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// Score panels row
// ============================================================

export const ScoresRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 32px;

  @media print {
    display: grid !important;
    grid-template-columns: repeat(4, 1fr) !important;
    gap: 16px !important;
    margin-top: 32px !important;
  }
`

export const ScorePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 0.5px solid ${omniaTheme.colors.border};

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
    padding-bottom: 12px !important;
    border-bottom: 0.5px solid ${omniaTheme.colors.border} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const ScoreValue = styled.div`
  font-family: ${omniaTheme.fonts.heading};
  font-weight: ${omniaTheme.fontWeights.light};
  font-size: 30px;
  color: ${omniaTheme.colors.black};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    font-size: 30px !important;
    color: ${omniaTheme.colors.black} !important;
    letter-spacing: ${omniaTheme.letterSpacing.tight} !important;
    line-height: 1 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const ScoreLabel = styled.div`
  font-family: ${omniaTheme.fonts.body};
  font-weight: ${omniaTheme.fontWeights.normal};
  font-size: 11px;
  color: ${omniaTheme.colors.text};

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-weight: ${omniaTheme.fontWeights.normal} !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.text} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`
